import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { crearEventoBoletera } from '../../../../service/boletera/EventosBoletera';
import { obtenerRecintoSelect, crearRecintoService } from '../../../../service/recintos/Recintos';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
};

const schema = Yup.object().shape({
  nombre: Yup.string().trim().required('El nombre del evento es requerido.'),
  tipo: Yup.string().nullable(),
  recinto_id: Yup.number().nullable(),
  fecha_inicio: Yup.string().required('La fecha y hora de inicio es requerida.'),
  aforo_max_evento: Yup.number()
    .typeError('Debe ser un número.')
    .positive('Debe ser mayor a cero.')
    .integer('Debe ser un número entero.')
    .required('El aforo máximo es requerido.'),
});

const ModalNuevoEvento: React.FC<Props> = ({ show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [recintoOpts, setRecintoOpts] = useState<Array<{ value: number; label: string }>>([]);
  const [showNuevoRecinto, setShowNuevoRecinto] = useState(false);
  const [nuevoRecinto, setNuevoRecinto] = useState({ nombre: '', direccion: '', aforo_max: '' });
  const [savingRecinto, setSavingRecinto] = useState(false);

  useEffect(() => {
    obtenerRecintoSelect().then(setRecintoOpts).catch(() => {});
  }, []);

  const handleCrearRecinto = async () => {
    if (!nuevoRecinto.nombre.trim()) { SimpleAlert('El nombre es requerido.', 3000, 'error'); return; }
    if (!nuevoRecinto.aforo_max || Number(nuevoRecinto.aforo_max) <= 0) { SimpleAlert('El aforo máximo es requerido.', 3000, 'error'); return; }
    setSavingRecinto(true);
    try {
      const resp = await crearRecintoService({ nombre: nuevoRecinto.nombre, direccion: nuevoRecinto.direccion, aforo_max: Number(nuevoRecinto.aforo_max) });
      const newId = resp?.data?.doc?.data?.id;
      const opts = await obtenerRecintoSelect();
      setRecintoOpts(opts);
      if (newId) form.setFieldValue('recinto_id', newId);
      setShowNuevoRecinto(false);
      setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' });
      SimpleAlert('Recinto creado y seleccionado', 2500, 'success');
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message || 'Error al crear el recinto.', 3000, 'error');
    } finally {
      setSavingRecinto(false);
    }
  };

  const form = useFormik({
    initialValues: {
      nombre: '',
      tipo: '',
      recinto_id: '' as any,
      fecha_inicio: '',
      aforo_max_evento: '' as any,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await crearEventoBoletera({
          nombre: values.nombre,
          tipo: values.tipo || undefined,
          recinto_id: values.recinto_id ? Number(values.recinto_id) : undefined,
          fecha_inicio: values.fecha_inicio,
          aforo_max_evento: Number(values.aforo_max_evento),
        });
        SimpleAlert('Evento creado en borrador', 2500, 'success');
        form.resetForm();
        handleClose(true);
      } catch (error: any) {
        SimpleAlert(error?.response?.data?.message || 'Error al crear el evento.', 3000, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
    <Modal
      id="kt_modal_nuevo_evento_boletera"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-550px"
      show={show}
      onHide={() => { form.resetForm(); handleClose(false); }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">Nuevo evento</h2>
          <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { form.resetForm(); handleClose(false); }}>
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form onSubmit={form.handleSubmit}>
            {/* Nombre */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Nombre del evento</label>
              <input className="form-control" placeholder="Ej: Noches de Rock — Gira 2026" {...form.getFieldProps('nombre')} />
              {form.touched.nombre && form.errors.nombre && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.nombre}</span></div>
              )}
            </div>
            {/* Tipo */}
            <div className="mb-5">
              <label className="fw-bold fs-6 mb-2">Tipo</label>
              <input className="form-control" placeholder="Concierto, Congreso, etc." {...form.getFieldProps('tipo')} />
            </div>
            {/* Recinto */}
            <div className="mb-5">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="fw-bold fs-6">Recinto</label>
                <button type="button" className="btn btn-link btn-sm p-0 text-primary" onClick={() => setShowNuevoRecinto(true)}>
                  + Nuevo recinto
                </button>
              </div>
              <select
                className="form-select"
                value={form.values.recinto_id}
                onChange={(e) => form.setFieldValue('recinto_id', e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— Seleccionar recinto —</option>
                {recintoOpts.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            {/* Fecha y hora de inicio */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Fecha y hora de inicio</label>
              <input className="form-control" type="datetime-local" {...form.getFieldProps('fecha_inicio')} />
              {form.touched.fecha_inicio && form.errors.fecha_inicio && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.fecha_inicio}</span></div>
              )}
            </div>
            {/* Aforo máximo */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Aforo máximo del evento</label>
              <input className="form-control" type="number" min={1} placeholder="Ej: 5000" {...form.getFieldProps('aforo_max_evento')} />
              {form.touched.aforo_max_evento && form.errors.aforo_max_evento && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.aforo_max_evento as string}</span></div>
              )}
            </div>

            <div className="d-flex justify-content-end mt-6">
              <button type="reset" className="btn btn-light me-3" onClick={() => { form.resetForm(); handleClose(false); }}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Espere...<span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  <span className="indicator-label">Crear (borrador)</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>

    {/* ── Mini-modal: Nuevo recinto ── */}
    <Modal
      backdrop="static"
      dialogClassName="modal-dialog mw-500px"
      show={showNuevoRecinto}
      onHide={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">Nuevo recinto</h2>
          <button type="button" className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>✕</button>
        </div>
        <div className="modal-body mx-5">
          <div className="mb-4">
            <label className="required fw-bold fs-6 mb-2">Nombre</label>
            <input className="form-control" placeholder="Ej: Cintermex" maxLength={100} value={nuevoRecinto.nombre} onChange={(e) => setNuevoRecinto((p) => ({ ...p, nombre: e.target.value }))} />
          </div>
          <div className="mb-4">
            <label className="fw-bold fs-6 mb-2">Dirección</label>
            <input className="form-control" placeholder="Ej: Av. Fundidora 501, Monterrey" maxLength={255} value={nuevoRecinto.direccion} onChange={(e) => setNuevoRecinto((p) => ({ ...p, direccion: e.target.value }))} />
          </div>
          <div className="mb-4">
            <label className="required fw-bold fs-6 mb-2">Aforo máximo (personas)</label>
            <input className="form-control" type="number" min={1} placeholder="Ej: 8000" value={nuevoRecinto.aforo_max} onChange={(e) => setNuevoRecinto((p) => ({ ...p, aforo_max: e.target.value }))} />
          </div>
          <div className="d-flex justify-content-end mt-6">
            <button type="button" className="btn btn-light me-3" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={handleCrearRecinto} disabled={savingRecinto}>
              {savingRecinto ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</> : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
};

export { ModalNuevoEvento };
